import type { ArtDate } from "./types"

/** Today's date in the browser's local timezone, at full precision. Typed
 *  with non-null parts (still assignable to ArtDate) so callers comparing
 *  against "today" don't need null checks. */
export function todayArtDate(): { year: number; month: number; day: number } {
	const d = new Date()
	return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() }
}

/** Localized full month name for a 1-based month number. */
export function monthName(month: number): string {
	return new Date(2000, month - 1, 1).toLocaleDateString(undefined, {
		month: "long",
	})
}

export function daysInMonth(year: number, month: number): number {
	return new Date(year, month, 0).getDate()
}

/** "Jul 12, 2023" / "Jul 2023" / "2023" / "Undated", by precision. */
export function friendlyArtDate(d: ArtDate): string {
	if (d.year === null) return "Undated"
	if (d.month === null) return String(d.year)
	return new Date(d.year, d.month - 1, d.day ?? 1).toLocaleDateString(
		undefined,
		d.day === null
			? { month: "short", year: "numeric" }
			: { month: "short", day: "numeric", year: "numeric" },
	)
}

/** Section label for the box grid: "July 2026" / "2026" / "Undated". */
export function artDateGroupLabel(d: ArtDate): string {
	if (d.year === null) return "Undated"
	if (d.month === null) return String(d.year)
	return new Date(d.year, d.month - 1, 1).toLocaleDateString(undefined, {
		month: "long",
		year: "numeric",
	})
}

export interface LabelGroup<T> {
	label: string
	items: T[]
}

/** Groups items into contiguous runs sharing the same label. Assumes items
 *  already arrive sorted — the API returns artworks date desc with undated
 *  last, so grouping while walking the list preserves that order. */
export function groupByLabel<T>(
	items: T[],
	getLabel: (item: T) => string,
): LabelGroup<T>[] {
	const groups: LabelGroup<T>[] = []
	for (const item of items) {
		const label = getLabel(item)
		const last = groups[groups.length - 1]
		if (last && last.label === label) {
			last.items.push(item)
		} else {
			groups.push({ label, items: [item] })
		}
	}
	return groups
}
